import React from 'react';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Privacy Policy</h1>
          <p className="text-sm text-gray-600 mb-8">Effective Date: {new Date().toLocaleDateString()}</p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-700 mb-2">Personal Information</h3>
                  <p className="text-gray-600">
                    When you create an account with Good Parents Justice, we collect:
                  </p>
                  <ul className="list-disc list-inside text-gray-600 mt-2 space-y-1">
                    <li>Name and email address</li>
                    <li>Phone number (optional)</li>
                    <li>Case information you choose to enter</li>
                    <li>Form data and legal document information</li>
                    <li>Calendar events and reminders you create</li>
                    <li>Communications with our AI assistant</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-700 mb-2">Automatically Collected Information</h3>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>IP address and location information</li>
                    <li>Browser type and version</li>
                    <li>Pages visited and time spent on our platform</li>
                    <li>Device information</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-600 mb-4">We use your information to:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Provide legal information and guidance services</li>
                <li>Enable AI-powered form completion assistance</li>
                <li>Send important reminders about deadlines and court dates</li>
                <li>Improve our platform and develop new features</li>
                <li>Ensure platform security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. Information Sharing and Disclosure</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 font-medium">
                  We do not sell, trade, or share your personal information with third parties for marketing purposes.
                </p>
              </div>
              <p className="text-gray-600 mb-4">We may share information only in these limited circumstances:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>With your explicit consent</li>
                <li>To comply with legal obligations or court orders</li>
                <li>To protect our rights or the safety of our users</li>
                <li>With service providers who help operate our platform (under strict confidentiality agreements)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Data Security</h2>
              <p className="text-gray-600 mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>SSL encryption for all data transmission</li>
                <li>Secure database storage with encryption</li>
                <li>Regular security audits and updates</li>
                <li>Limited access controls for our staff</li>
                <li>Secure authentication and session management</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Your Rights Under Canadian Privacy Laws</h2>
              <p className="text-gray-600 mb-4">
                Under the Personal Information Protection and Electronic Documents Act (PIPEDA), you have the right to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Withdraw consent for data processing</li>
                <li>File a complaint with the Privacy Commissioner of Canada</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Data Retention</h2>
              <p className="text-gray-600">
                We retain your information for as long as your account is active or as needed to provide services. 
                You may request deletion of your account and associated data at any time by contacting us at 
                privacy@goodparentsjustice.com.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Contact Information</h2>
              <p className="text-gray-600">
                For questions about this privacy policy or to exercise your privacy rights, contact us at:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <p className="text-gray-700">
                  <strong>Good Parents Justice</strong><br />
                  Email: privacy@goodparentsjustice.com<br />
                  Subject Line: Privacy Inquiry
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Changes to This Policy</h2>
              <p className="text-gray-600">
                We may update this privacy policy from time to time. We will notify you of significant changes 
                by email or through our platform. Your continued use of our services after such modifications 
                constitutes your acceptance of the updated policy.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Terms of Service</h1>
          <p className="text-sm text-gray-600 mb-8">Effective Date: {new Date().toLocaleDateString()}</p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600">
                By accessing and using Good Parents Justice ("the Platform"), you accept and agree to be bound by the terms
                and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Description of Service</h2>
              <p className="text-gray-600 mb-4">
                Good Parents Justice provides legal information and self-help tools for Ontario family law matters, including:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Legal information and educational resources</li>
                <li>AI-powered form completion assistance</li>
                <li>Calendar and deadline tracking tools</li>
                <li>Access to legal forms and templates</li>
                <li>General guidance on family law procedures</li>
              </ul>
            </section>

            <section>
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h2 className="text-2xl font-semibold text-red-800 mb-4">⚠️ IMPORTANT LEGAL DISCLAIMER</h2>
                <div className="space-y-3 text-red-700">
                  <p className="font-medium">THIS PLATFORM DOES NOT PROVIDE LEGAL ADVICE</p>
                  <p>
                    Good Parents Justice provides general legal information only. This information is not intended to be used as
                    legal advice for a specific legal problem. The information provided should not be used as a substitute for 
                    competent legal advice from a licensed attorney in your jurisdiction.
                  </p>
                  <p>
                    <strong>No Attorney-Client Relationship:</strong> Use of this platform does not create an attorney-client 
                    relationship between you and Good Parents Justice or any of its contributors.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. User Responsibilities</h2>
              <p className="text-gray-600 mb-4">By using our platform, you agree to:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Provide accurate and truthful information</li>
                <li>Use the platform only for lawful purposes</li>
                <li>Not share your account credentials with others</li>
                <li>Not attempt to harm or exploit our platform or other users</li>
                <li>Verify all information with qualified legal professionals</li>
                <li>Understand that AI assistance is for guidance only, not legal advice</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Limitation of Liability</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-yellow-800 font-medium">
                  Good Parents Justice is provided "as is" without warranty of any kind.
                </p>
              </div>
              <p className="text-gray-600 mb-4">
                To the fullest extent permitted by law, Good Parents Justice disclaims all warranties and shall not be liable for:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Any errors or omissions in the information provided</li>
                <li>Any loss or damage arising from use of our platform</li>
                <li>Missed deadlines or court dates</li>
                <li>Consequences of legal decisions made based on our information</li>
                <li>Technical issues or platform downtime</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Ontario Jurisdiction</h2>
              <p className="text-gray-600">
                This platform is designed specifically for Ontario family law and should not be used for legal matters in other 
                jurisdictions. Laws vary by location, and information provided may not be applicable outside of Ontario, Canada.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Account Termination</h2>
              <p className="text-gray-600">
                We reserve the right to terminate or suspend accounts that violate these terms or engage in harmful behavior. 
                You may delete your account at any time through your account settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Changes to Terms</h2>
              <p className="text-gray-600">
                We may modify these terms at any time. Significant changes will be communicated via email or platform 
                notifications. Continued use of the platform after changes constitutes acceptance of new terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Contact Information</h2>
              <p className="text-gray-600">
                For questions about these terms of service, contact us at:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <p className="text-gray-700">
                  <strong>Good Parents Justice</strong><br />
                  Email: legal@goodparentsjustice.com<br />
                  Subject Line: Terms of Service Inquiry
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export const LegalDisclaimer = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Legal Disclaimer</h1>
          
          <div className="space-y-8">
            <div className="bg-red-50 border-l-4 border-red-500 p-6">
              <h2 className="text-xl font-bold text-red-800 mb-4">⚠️ IMPORTANT - READ BEFORE USING</h2>
              <p className="text-red-700 font-medium">
                This platform provides general legal information only. It is not a substitute for legal advice from a qualified lawyer.
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">What This Platform IS</h2>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>A source of general legal information about Ontario family law</li>
                <li>A tool to help you understand legal processes and forms</li>
                <li>An AI-powered assistant to guide you through common legal procedures</li>
                <li>A resource for finding legal forms and court information</li>
                <li>Educational content about family law topics</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">What This Platform IS NOT</h2>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>A law firm or legal service provider</li>
                <li>A substitute for legal advice from a qualified lawyer</li>
                <li>Able to provide advice for your specific legal situation</li>
                <li>Responsible for the outcome of your legal matter</li>
                <li>A guarantee of legal success or favorable outcomes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">When to Seek Legal Advice</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 font-medium mb-3">You should consult with a qualified lawyer if:</p>
                <ul className="list-disc list-inside text-blue-700 space-y-1">
                  <li>Your case involves complex legal issues</li>
                  <li>Significant assets or custody disputes are involved</li>
                  <li>You face domestic violence or safety concerns</li>
                  <li>You're considering an appeal</li>
                  <li>You're unsure about your legal rights or obligations</li>
                  <li>Court deadlines are approaching</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">AI Assistant Limitations</h2>
              <p className="text-gray-600 mb-4">Our AI assistant:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Provides general guidance based on common scenarios</li>
                <li>Cannot analyze the specific facts of your case</li>
                <li>May not have access to the most recent legal changes</li>
                <li>Cannot replace professional legal judgment</li>
                <li>Should not be relied upon for critical legal decisions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Your Responsibility</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800">
                  <strong>You are responsible for:</strong>
                </p>
                <ul className="list-disc list-inside text-yellow-700 space-y-1 mt-2">
                  <li>Verifying all information with appropriate legal authorities</li>
                  <li>Meeting all court deadlines and requirements</li>
                  <li>Understanding the consequences of legal decisions</li>
                  <li>Seeking professional help when needed</li>
                  <li>Ensuring forms are completed accurately</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Emergency Situations</h2>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 font-medium">For immediate safety concerns or emergencies:</p>
                <ul className="list-disc list-inside text-red-700 space-y-1 mt-2">
                  <li><strong>Emergency:</strong> Call 911</li>
                  <li><strong>Crisis Support:</strong> 1-866-863-0511 (Assaulted Women's Helpline)</li>
                  <li><strong>Kids Help Phone:</strong> 1-800-668-6868</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Legal Resources</h2>
              <p className="text-gray-600 mb-4">For legal assistance in Ontario:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li><strong>Legal Aid Ontario:</strong> 1-800-668-8258</li>
                <li><strong>Law Society of Ontario:</strong> Lawyer referral service</li>
                <li><strong>Pro Bono Ontario:</strong> Free legal clinics</li>
                <li><strong>Family Law Information Centres:</strong> Available at court locations</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
